import React, { useEffect, useState } from 'react';
import { Button, Callout, Card, Flex, Heading, Text, TextField } from '@radix-ui/themes';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { VedtaegtDoc, VedtaegtSection } from '../../types/app';
import MaterialIcon from '../common/MaterialIcon';

type VedtaegterTabProps = {
  hasAppAccess: boolean;
  currentUserId: string | null;
};

const createNodeId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createEmptySection = (): VedtaegtSection => ({
  id: createNodeId(),
  text: '',
  children: [],
});

const normalizeSections = (rawValue: unknown): VedtaegtSection[] => {
  if (!Array.isArray(rawValue)) {
    return [];
  }

  const normalizeNode = (value: unknown): VedtaegtSection | null => {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const rawNode = value as {
      id?: unknown;
      text?: unknown;
      children?: unknown;
    };

    const nodeId = typeof rawNode.id === 'string' && rawNode.id.trim() ? rawNode.id : createNodeId();
    const nodeText = typeof rawNode.text === 'string' ? rawNode.text : '';

    return {
      id: nodeId,
      text: nodeText,
      children: normalizeSections(rawNode.children),
    };
  };

  return rawValue
    .map((item) => normalizeNode(item))
    .filter((item): item is VedtaegtSection => Boolean(item));
};

const updateSectionText = (
  sections: VedtaegtSection[],
  sectionId: string,
  nextText: string
): VedtaegtSection[] => {
  return sections.map((section) => {
    if (section.id === sectionId) {
      return { ...section, text: nextText };
    }

    return {
      ...section,
      children: updateSectionText(section.children, sectionId, nextText),
    };
  });
};

const addChildSection = (sections: VedtaegtSection[], parentId: string): VedtaegtSection[] => {
  return sections.map((section) => {
    if (section.id === parentId) {
      return {
        ...section,
        children: [...section.children, createEmptySection()],
      };
    }

    return {
      ...section,
      children: addChildSection(section.children, parentId),
    };
  });
};

const removeSectionById = (sections: VedtaegtSection[], sectionId: string): VedtaegtSection[] => {
  return sections
    .filter((section) => section.id !== sectionId)
    .map((section) => ({
      ...section,
      children: removeSectionById(section.children, sectionId),
    }));
};

function VedtaegterTab({ hasAppAccess, currentUserId }: VedtaegterTabProps) {
  const [sections, setSections] = useState<VedtaegtSection[]>([]);
  const [isLoadingVedtaegter, setIsLoadingVedtaegter] = useState(false);
  const [isSavingVedtaegter, setIsSavingVedtaegter] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [vedtaegterError, setVedtaegterError] = useState('');
  const [vedtaegterSuccess, setVedtaegterSuccess] = useState('');

  useEffect(() => {
    if (!hasAppAccess) {
      setSections([]);
      return;
    }

    setIsLoadingVedtaegter(true);
    setVedtaegterError('');

    const vedtaegterDocRef = doc(db, 'vedtaegter', 'current');
    const unsubscribe = onSnapshot(
      vedtaegterDocRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setSections([]);
          setIsLoadingVedtaegter(false);
          return;
        }

        const data = snapshot.data() as VedtaegtDoc;
        setSections(normalizeSections(data.sections));
        setIsLoadingVedtaegter(false);
      },
      () => {
        setVedtaegterError('Kunne ikke hente vedtægter.');
        setIsLoadingVedtaegter(false);
      }
    );

    return () => unsubscribe();
  }, [hasAppAccess]);

  const handleAddTopLevelSection = () => {
    setVedtaegterError('');
    setVedtaegterSuccess('');
    setIsEditMode(true);
    setSections((previousSections) => [...previousSections, createEmptySection()]);
  };

  const handleAddChildSection = (parentId: string) => {
    setVedtaegterError('');
    setVedtaegterSuccess('');
    setIsEditMode(true);
    setSections((previousSections) => addChildSection(previousSections, parentId));
  };

  const handleUpdateSectionText = (sectionId: string, nextText: string) => {
    setVedtaegterSuccess('');
    setIsEditMode(true);
    setSections((previousSections) => updateSectionText(previousSections, sectionId, nextText));
  };

  const handleDeleteSection = (sectionId: string) => {
    setVedtaegterError('');
    setVedtaegterSuccess('');
    setIsEditMode(true);
    setSections((previousSections) => removeSectionById(previousSections, sectionId));
  };

  const handleSaveVedtaegter = async () => {
    if (!hasAppAccess || !currentUserId) {
      setVedtaegterError('Du har ikke adgang til at gemme vedtægter.');
      return;
    }

    setVedtaegterError('');
    setVedtaegterSuccess('');
    setIsSavingVedtaegter(true);

    try {
      await setDoc(
        doc(db, 'vedtaegter', 'current'),
        {
          title: 'Vedtægter',
          sections,
          updatedBy: currentUserId,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setVedtaegterSuccess('Vedtægter er gemt.');
    } catch (error: unknown) {
      setVedtaegterError('Kunne ikke gemme vedtægter.');
    } finally {
      setIsSavingVedtaegter(false);
    }
  };

  const renderSection = (section: VedtaegtSection, path: number[]): React.ReactNode => {
    const sectionNumber = path.join('.');
    const level = path.length;

    return (
      <div key={section.id} className="grid gap-3" style={{ marginLeft: `${Math.min((level - 1) * 20, 120)}px` }}>
        <Card className="rounded-2xl border border-slate-200 p-3">
          <Flex direction="column" gap="2">
            <Text size="1" weight="bold" className="uppercase tracking-[0.18em] text-pine-700">
              {level === 1 ? `Paragraf ${sectionNumber}` : `Underpunkt ${sectionNumber}`}
            </Text>

            <TextField.Root
              value={section.text}
              onChange={(event) => handleUpdateSectionText(section.id, event.target.value)}
              placeholder={level === 1 ? 'Skriv paragraftekst' : 'Skriv underpunktstekst'}
            />

            <Flex gap="2" wrap="wrap">
              <Button type="button" variant="soft" color="gray" highContrast onClick={() => handleAddChildSection(section.id)}>
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name="add" className="text-[18px]" />
                  Tilføj underpunkt
                </span>
              </Button>
              <Button type="button" variant="soft" color="red" highContrast onClick={() => handleDeleteSection(section.id)}>
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name="delete" className="text-[18px]" />
                  Slet punkt
                </span>
              </Button>
            </Flex>
          </Flex>
        </Card>

        {section.children.map((childSection, childIndex) => renderSection(childSection, [...path, childIndex + 1]))}
      </div>
    );
  };

  const renderLegalPrefix = (path: number[]): string => {
    if (path.length === 1) {
      return `§ ${path[0]}.`;
    }

    if (path.length === 2) {
      return `Stk. ${path[1]}.`;
    }

    if (path.length === 3) {
      return `Nr. ${path[2]}.`;
    }

    return `${path.slice(2).join('.')}.`;
  };

  const renderReadOnlySection = (section: VedtaegtSection, path: number[]): React.ReactNode => {
    const level = path.length;
    const prefix = renderLegalPrefix(path);

    return (
      <div key={section.id} className="grid gap-2" style={{ marginLeft: `${Math.min((level - 1) * 22, 132)}px` }}>
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <Text
            size={level === 1 ? '3' : '2'}
            weight={level === 1 ? 'bold' : 'medium'}
            className="font-serif text-slate-900"
          >
            {prefix}
          </Text>
          <Text
            size={level === 1 ? '3' : '2'}
            className="font-serif leading-7 text-slate-800"
          >
            {section.text.trim() || '................................'}
          </Text>
        </div>

        {section.children.map((childSection, childIndex) => renderReadOnlySection(childSection, [...path, childIndex + 1]))}
      </div>
    );
  };

  return (
    <>
      <Heading size="6" className="font-display text-pine-900">
        <span className="inline-flex items-center gap-2">
          <MaterialIcon name="gavel" className="text-[24px] text-pine-700" />
          Vedtægter
        </span>
      </Heading>
      <Text size="2" color="gray" className="mt-2 block">
        Tilføj paragraffer og ubegrænsede underpunkter, og gem dem samlet ét sted.
      </Text>

      <Card className="mt-4 rounded-2xl border border-pine-100 p-4">
        <Flex gap="2" wrap="wrap">
          {isEditMode ? (
            <>
              <Button type="button" variant="solid" color="grass" highContrast onClick={handleAddTopLevelSection}>
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name="add_circle" className="text-[18px]" />
                  Tilføj paragraf
                </span>
              </Button>
              <Button
                type="button"
                variant="soft"
                color="gray"
                highContrast
                onClick={() => void handleSaveVedtaegter()}
                disabled={isSavingVedtaegter || isLoadingVedtaegter}
              >
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name={isSavingVedtaegter ? 'hourglass_top' : 'save'} className="text-[18px]" />
                  {isSavingVedtaegter ? 'Gemmer vedtægter...' : 'Gem vedtægter'}
                </span>
              </Button>
              <Button type="button" variant="outline" color="gray" highContrast onClick={() => setIsEditMode(false)}>
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name="visibility" className="text-[18px]" />
                  Vis dokument
                </span>
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="solid" color="gray" highContrast onClick={() => setIsEditMode(true)}>
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name="edit_document" className="text-[18px]" />
                  Redigér vedtægter
                </span>
              </Button>
              <Button
                type="button"
                variant="soft"
                color="gray"
                highContrast
                onClick={() => void handleSaveVedtaegter()}
                disabled={isSavingVedtaegter || isLoadingVedtaegter}
              >
                <span className="inline-flex items-center gap-1.5">
                  <MaterialIcon name={isSavingVedtaegter ? 'hourglass_top' : 'save'} className="text-[18px]" />
                  {isSavingVedtaegter ? 'Gemmer vedtægter...' : 'Gem vedtægter'}
                </span>
              </Button>
            </>
          )}
        </Flex>

        {isLoadingVedtaegter ? (
          <Text size="2" color="gray" className="mt-3 block">
            Henter vedtægter...
          </Text>
        ) : !sections.length ? (
          <Text size="2" color="gray" className="mt-3 block rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            Ingen paragraffer endnu. Start med at tilføje den første paragraf.
          </Text>
        ) : isEditMode ? (
          <div className="mt-3 grid gap-3">{sections.map((section, index) => renderSection(section, [index + 1]))}</div>
        ) : (
          <Card className="mt-3 rounded-2xl border border-slate-300 bg-slate-50 p-5">
            <Heading size="4" className="text-center font-serif tracking-[0.22em] text-slate-800">
              VEDTÆGTER
            </Heading>
            <div className="mt-5 grid gap-5">{sections.map((section, index) => renderReadOnlySection(section, [index + 1]))}</div>
          </Card>
        )}

        {vedtaegterError && (
          <Callout.Root color="red" variant="soft" className="mt-4">
            <Callout.Text>{vedtaegterError}</Callout.Text>
          </Callout.Root>
        )}

        {vedtaegterSuccess && (
          <Callout.Root color="grass" variant="soft" className="mt-4">
            <Callout.Text>{vedtaegterSuccess}</Callout.Text>
          </Callout.Root>
        )}
      </Card>
    </>
  );
}

export default VedtaegterTab;
